import { Link, router, useForm, usePage } from "@inertiajs/react";
import { type FormEvent, type ReactNode, useState } from "react";
import { toast } from "sonner";

import decoratorWhite from "@/assets/decorator_white.svg";
import Button from "@/components/Button";
import ConfirmDialog from "@/components/ConfirmDialog";
import type { Column, RowAction } from "@/components/DataTable";
import DataTable from "@/components/DataTable";
import LinkButton from "@/components/LinkButton";
import Modal from "@/components/Modal";
import SearchableSelect from "@/components/SearchableSelect";
import { formatDateTimeRange } from "@/lib/formatting";
import { useGenderedText } from "@/lib/genderedText";
import {
  canCloseCourse,
  canReopenCourse,
  usePermissions,
} from "@/lib/permissions";
import {
  closeCourse,
  editCourse,
  enrollments,
  exportParticipantsExcelCourse,
  reopenCourse,
  sendConfirmationEmailsCourse,
  uploadClosingFileCourse,
  users as userRoutes,
  waitLists,
} from "@/routes";
import type {
  Course,
  CourseForm,
  Enrollment,
  SharedProps,
  WaitList,
} from "@/types";

type ConfirmConfig = {
  title: string;
  message: string;
  confirmLabel: string;
  variant: "primary" | "danger";
  onConfirm: () => void;
};

type Props = {
  data: Course;
  enrollments: Enrollment[];
  wait_lists: WaitList[];
  diet_options: string[];
  diet_summary?: Record<string, number>;
  users: { id: number; name: string; rut: string }[];
  first_reminder_day: string | null;
  second_reminder_day: string | null;
  course_form: CourseForm | null;
  closing_file?: { filename: string; url: string } | null;
  // Set when reached from Resumen general (courses/statistics/:id): a
  // reporting view, so no management action (edit/close/enroll/etc.) shows.
  read_only?: boolean;
};

// Same wording as the questions asked in Courses/Close.tsx, so the answers
// read back next to the question that was actually asked.
const COURSE_FORM_QUESTIONS: { key: keyof CourseForm; label: string }[] = [
  {
    key: "food_comment",
    label: "¿Tienes algún comentario respecto a la Comida?",
  },
  {
    key: "cleaning_comment",
    label: "¿Tienes algún comentario respecto al Aseo?",
  },
  {
    key: "administration_comment",
    label: "¿Tienes algún comentario respecto a la Administración?",
  },
  {
    key: "house_comment",
    label: "¿Tienes algún comentario respecto a la Casa en General?",
  },
  {
    key: "general_comment",
    label: "Si tienes algún comentario o pregunta, escríbelo a continuación",
  },
];

const DIET_LABELS: Record<string, string> = {
  no_diet: "Sin dieta especial",
  no_gluten: "Sin gluten",
  no_lactose: "Sin lactosa",
  no_salt: "Sin sal",
  diabetic: "Diabético",
};

const DEFAULT_DIET = "no_diet";

const INSCRIPTION_STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  confirmed: "Confirmada",
};

const USER_CATEGORY_LABELS: Record<string, string> = {
  senora: "Señora",
  universitaria: "Universitaria",
  numeraria_auxiliar: "Numeraria Auxiliar",
  agregada: "Agregada",
  numeraria: "Numeraria",
  la_cuesta: "S V",
  la_cuesta_sn: "S SV",
  la_cuesta_cp: "CP SV",
};

function formatDate(date: string | null): string {
  if (!date) return "-";
  const [year, month, day] = date.split("-").map(Number);
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
}

function formatDateTime(dateTime: string | null): string {
  if (!dateTime) return "-";
  const d = new Date(dateTime);
  return `${formatDate(dateTime.slice(0, 10))} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function formatAmount(amount: number | null): string {
  if (amount == null) return "-";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(amount);
}

function daysBetween(from: Date, to: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round(
    (Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) -
      Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) /
      msPerDay,
  );
}

function formatReminderStatus(reminderDay: string | null): string {
  if (!reminderDay) return "-";
  const [year, month, day] = reminderDay.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  const today = new Date();
  const diff = daysBetween(date, today);

  if (diff >= 0) {
    return `Enviado (hace ${diff === 0 ? "menos de un día" : `${diff} día${diff === 1 ? "" : "s"}`})`;
  }
  const remaining = -diff;
  return `${formatDate(reminderDay)} (en ${remaining} día${remaining === 1 ? "" : "s"})`;
}

function formatDiet(diet: unknown): string {
  const options = Array.isArray(diet) ? diet : diet ? [diet] : [];
  return options.map((o: string) => DIET_LABELS[o] ?? o).join(", ") || "-";
}

function InfoSection({
  icon,
  title,
  action,
  children,
}: {
  icon: ReactNode;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4 rounded-2xl border-2 border-ink/20 p-5">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-sans text-sm font-semibold text-ink">
          <span className="text-steel">{icon}</span>
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

// Hardcoded to mirror app/views/user_notifier/_payment_info.html.erb exactly —
// la_cuesta houses generally carry no payment data of their own, except house 58,
// which uses its own data via CourseResource#house_payment instead.
const LA_CUESTA_PAYMENT_INFO = {
  bank: "Banco BCI",
  rut: "65.566.980-9",
  name: "Fundación Educacional La Cuesta.",
  accountType: "Cuenta Corriente",
  accountNumber: "29429978",
  email: "contacto@retirosycursos.cl",
};

function paymentInfoText(payment: {
  bank: string | null;
  rut: string | null;
  name: string | null;
  accountType: string | null;
  accountNumber: string | null;
  email: string | null;
  url?: string | null;
}): string {
  const lines = payment.url ? [`Link de pago: ${payment.url}`] : [];

  return lines
    .concat([
      `Banco: ${payment.bank ?? "-"}`,
      `Rut: ${payment.rut ?? "-"}`,
      `Nombre: ${payment.name ?? "-"}`,
      `Tipo de Cuenta: ${payment.accountType ?? "-"}`,
      `Número de Cuenta: ${payment.accountNumber ?? "-"}`,
      `Mail para comprobante: ${payment.email ?? "-"}`,
    ])
    .join("\n");
}

function paymentInfoFor(course: Course) {
  return course.origin === "la_cuesta" && course.house_id !== 58
    ? LA_CUESTA_PAYMENT_INFO
    : course.house_payment;
}

function titleCase(text: string): string {
  return text
    .toLowerCase()
    .replace(
      /(^|\s)(\S)/g,
      (_, space: string, char: string) => space + char.toUpperCase(),
    );
}

/** Plain-text summary mirroring the course details in the confirmation emails
 *  (app/views/user_notifier/_course_details.html.erb). */
function courseInfoText(course: Course): string {
  const lines = [
    titleCase((course.category_name as string) || "-"),
    `Fechas: ${formatDateTimeRange(course.start_date, course.end_date, course.start_time, course.end_time)}`,
    `Casa: ${(course.house_name as string) || "-"}`,
    `Lugar: ${(course.house_address as string) || "-"}`,
  ];
  if (course.house_address_url) {
    lines.push(`Cómo llegar: ${course.house_address_url}`);
  }
  lines.push(`Aporte: ${formatAmount(course.amount)}`);
  return lines.join("\n");
}

function CopyCourseInfoButton({ course }: { course: Course }) {
  async function copy() {
    await navigator.clipboard.writeText(courseInfoText(course));
    toast.success("Datos del curso copiados al portapapeles", {
      style: {
        background: "#F0FDF4",
        borderColor: "#16A34A",
        color: "#166534",
      },
      duration: 3000,
    });
  }

  return (
    <button
      type="button"
      onClick={copy}
      className="w-fit font-sans text-xs font-medium text-steel underline underline-offset-2 hover:text-steel/80"
    >
      Copiar datos del curso
    </button>
  );
}

function CopyPaymentInfoButton({ course }: { course: Course }) {
  const payment = paymentInfoFor(course);

  if (!payment) return null;
  const paymentInfo = payment;

  async function copy() {
    await navigator.clipboard.writeText(paymentInfoText(paymentInfo));
    toast.success("Datos de transferencia copiados al portapapeles", {
      style: {
        background: "#F0FDF4",
        borderColor: "#16A34A",
        color: "#166534",
      },
      duration: 3000,
    });
  }

  return (
    <button
      type="button"
      onClick={copy}
      className="w-fit font-sans text-xs font-medium text-steel underline underline-offset-2 hover:text-steel/80"
    >
      Copiar datos de transferencia
    </button>
  );
}

function Item({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="font-sans text-xs text-ink/50">{label}</dt>
      <dd className="font-sans text-sm text-ink">{children}</dd>
    </div>
  );
}

const STAT_BOX_TONE_CLASSES = {
  neutral: "bg-ink/5",
  highlight: "bg-olive/20",
  success: "bg-green-100",
  warning: "bg-amber-100",
  danger: "bg-red-100",
} as const;

function StatBox({
  value,
  label,
  tone = "neutral",
}: {
  value: ReactNode;
  label: string;
  tone?: keyof typeof STAT_BOX_TONE_CLASSES;
}) {
  return (
    <div
      className={`flex flex-col items-center gap-1 rounded-xl px-3 py-4 text-center ${STAT_BOX_TONE_CLASSES[tone]}`}
    >
      <span className="font-sans text-2xl font-semibold text-ink">{value}</span>
      <span className="font-sans text-xs text-ink/60">{label}</span>
    </div>
  );
}

/** Contribution progress: sum of paid amounts vs. amount * number of enrolled. */
function contributionPercentage(
  totalPaidAmount: number | null,
  amount: number | null,
  enrolledCount: number | null,
): number {
  const target = (amount ?? 0) * (enrolledCount ?? 0);
  if (target <= 0) return 0;
  return Math.round(((totalPaidAmount ?? 0) / target) * 100);
}

function contributionTone(
  percentage: number,
): keyof typeof STAT_BOX_TONE_CLASSES {
  if (percentage <= 0) return "danger";
  if (percentage >= 100) return "success";
  return "warning";
}

function StatusBadge({ active, label }: { active: boolean; label: string }) {
  return (
    <span
      className={`w-fit rounded-full px-3 py-1 font-sans text-xs ${
        active ? "bg-olive/30 text-ink" : "bg-ink/10 text-ink/50"
      }`}
    >
      {active ? "✓" : "✕"} {label}
    </span>
  );
}

export default function CoursesShow({
  data: course,
  enrollments: enrollmentRows,
  wait_lists: waitListRows,
  diet_options,
  diet_summary,
  users,
  first_reminder_day,
  second_reminder_day,
  course_form,
  closing_file,
  read_only: readOnly = false,
}: Props) {
  const {
    isAdmin,
    canSelfEnroll,
    canEditCourse,
    canAddPayment,
    canConfirmEnrollment,
    canDeleteEnrollment,
    canEnrollParticipants,
    canExportParticipants,
    canManageWaitList,
    canForceEnrollFromWaitList,
    canViewParticipantTables,
    canViewClosingQuestionnaire,
    canViewUserProfiles,
    isBackofficeStaff,
  } = usePermissions();
  const { currentUser } = usePage<SharedProps>().props;
  const showCloseButton = !readOnly && canCloseCourse(currentUser, course);
  // Reopening stays available even from the read-only Resumen general view —
  // otherwise a closed course found there would have no way back into Cursos.
  const showReopenButton = canReopenCourse(currentUser);
  const t = useGenderedText();

  function uploadClosingFile(file: File | undefined) {
    if (!file) return;
    router.post(
      uploadClosingFileCourse(course.id).url,
      { closing_file: file },
      { forceFormData: true, preserveScroll: true },
    );
  }

  const [paymentTarget, setPaymentTarget] = useState<Enrollment | null>(null);
  const [dietTarget, setDietTarget] = useState<Enrollment | null>(null);
  const [addEnrollmentOpen, setAddEnrollmentOpen] = useState(false);
  const [addWaitListOpen, setAddWaitListOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState<ConfirmConfig | null>(
    null,
  );

  function enroll() {
    router.post(
      enrollments.create().url,
      { enrollment: { course_id: course.id } },
      { preserveScroll: true },
    );
  }

  function confirmAttendance(enrollment: Enrollment) {
    setConfirmConfig({
      title: "Confirmar asistencia",
      message: `¿Confirmar la asistencia de ${enrollment.user_name ?? "este usuario"}?`,
      confirmLabel: "Confirmar",
      variant: "primary",
      onConfirm: () => {
        router.post(
          enrollments.backofficeConfirmation(enrollment.id).url,
          {},
          { preserveScroll: true },
        );
        setConfirmConfig(null);
      },
    });
  }

  function deleteEnrollment(enrollment: Enrollment) {
    setConfirmConfig({
      title: "Eliminar inscripción",
      message: `¿Estás seguro? Se eliminará a ${enrollment.user_name ?? "este usuario"} de la actividad.`,
      confirmLabel: "Eliminar",
      variant: "danger",
      onConfirm: () => {
        router.delete(enrollments.destroy(enrollment.id).url, {
          preserveScroll: true,
        });
        setConfirmConfig(null);
      },
    });
  }

  function promoteWaitList(waitList: WaitList) {
    setConfirmConfig({
      title: "Forzar inscripción",
      message:
        "¿Estás seguro? El usuario será inscrito aunque ya tenga un curso de este tipo.",
      confirmLabel: "Inscribir",
      variant: "primary",
      onConfirm: () => {
        router.post(
          waitLists.promote(waitList.id).url,
          {},
          {
            preserveScroll: true,
          },
        );
        setConfirmConfig(null);
      },
    });
  }

  function sendConfirmationEmailsConfirm() {
    setConfirmConfig({
      title: "Enviar emails de confirmación",
      message:
        "¿Estás seguro de enviar los emails de confirmación a todas las inscripciones pendientes?",
      confirmLabel: "Enviar",
      variant: "primary",
      onConfirm: () => {
        router.post(
          sendConfirmationEmailsCourse(course.id).url,
          {},
          { preserveScroll: true },
        );
        setConfirmConfig(null);
      },
    });
  }

  function reopenCourseConfirm() {
    setConfirmConfig({
      title: "Reabrir curso",
      message: "¿Estás seguro? Se volverán a permitir inscripciones y cambios.",
      confirmLabel: "Reabrir curso",
      variant: "primary",
      onConfirm: () => {
        router.post(reopenCourse(course.id).url, {}, { preserveScroll: true });
        setConfirmConfig(null);
      },
    });
  }

  function deleteWaitList(waitList: WaitList) {
    setConfirmConfig({
      title: "Eliminar de la lista de espera",
      message: `¿Estás seguro? Se eliminará a ${waitList.user_name ?? "este usuario"} de la lista de espera.`,
      confirmLabel: "Eliminar",
      variant: "danger",
      onConfirm: () => {
        router.delete(waitLists.destroy(waitList.id).url, {
          preserveScroll: true,
        });
        setConfirmConfig(null);
      },
    });
  }

  const emailCell = (row: { user_id: number | null; user_email?: unknown }) => {
    const email = (row.user_email as string) || "-";
    if (canViewUserProfiles && row.user_id) {
      return (
        <a
          href={userRoutes.profile(row.user_id).url}
          className="w-fit font-sans text-steel underline underline-offset-2 hover:text-steel/80"
        >
          {email}
        </a>
      );
    }
    return email;
  };

  const enrollmentColumns: Column<Enrollment>[] = [
    { header: "Nombre", cell: (e) => (e.user_name as string) || "-" },
    { header: "RUT", cell: (e) => (e.user_rut as string) || "-" },
    { header: "Email", cell: emailCell },
    { header: "Teléfono", cell: (e) => (e.user_phone as string) || "-" },
    {
      header: "Dieta",
      cell: (e) =>
        course.closed || readOnly ? (
          formatDiet(e.diet)
        ) : (
          <button
            type="button"
            onClick={() => setDietTarget(e)}
            className="w-fit text-left underline underline-offset-2 hover:text-steel"
          >
            {formatDiet(e.diet)}
          </button>
        ),
    },
    {
      header: "Estado",
      cell: (e) =>
        INSCRIPTION_STATUS_LABELS[e.inscription_status] ?? e.inscription_status,
    },
    { header: "Pagado", cell: (e) => formatAmount(e.paid_amount) },
    { header: "Inscrito el", cell: (e) => formatDateTime(e.created_at) },
  ];

  const enrollmentActions = (e: Enrollment): RowAction<Enrollment>[] => {
    if (course.closed || readOnly) return [];

    const actions: RowAction<Enrollment>[] = [];
    if (canAddPayment) {
      actions.push({
        label: "Abonar",
        onClick: () => setPaymentTarget(e),
        variant: "primary" as const,
      });
    }
    if (canDeleteEnrollment) {
      actions.push({
        label: "Eliminar",
        onClick: deleteEnrollment,
        variant: "danger" as const,
      });
    }
    // Reserved even when not pending (hidden, not omitted) so every row's
    // action group is the same width and they align with each other — see
    // RowAction.hidden.
    if (canConfirmEnrollment) {
      actions.push({
        label: "Confirmar",
        onClick: confirmAttendance,
        variant: "primary" as const,
        hidden: e.inscription_status !== "pending",
      });
    }
    return actions;
  };

  const waitListColumns: Column<WaitList>[] = [
    { header: "Nombre", cell: (w) => (w.user_name as string) || "-" },
    { header: "RUT", cell: (w) => (w.user_rut as string) || "-" },
    { header: "Email", cell: emailCell },
    { header: "Teléfono", cell: (w) => (w.user_phone as string) || "-" },
    { header: "Dieta", cell: (w) => formatDiet(w.diet) },
    { header: "En espera desde", cell: (w) => formatDateTime(w.created_at) },
  ];

  const waitListActions: RowAction<WaitList>[] =
    !course.closed && !readOnly
      ? [
          ...(canForceEnrollFromWaitList
            ? [
                {
                  label: "Forzar inscripción",
                  onClick: promoteWaitList,
                  variant: "primary" as const,
                },
              ]
            : []),
          ...(canManageWaitList
            ? [
                {
                  label: "Eliminar",
                  onClick: deleteWaitList,
                  variant: "danger" as const,
                },
              ]
            : []),
        ]
      : [];

  return (
    <>
      <div className="flex w-full flex-col gap-10 py-10">
        <div className="flex flex-col gap-4 px-6 sm:px-8 lg:px-12">
          <div className="flex flex-wrap items-center justify-end gap-4 self-end">
            {canEditCourse && !course.closed && !readOnly && (
              <a
                href={editCourse(course.id).url}
                className="w-fit font-sans text-sm text-steel underline underline-offset-2 hover:text-steel/80"
              >
                Editar curso
              </a>
            )}
            {showCloseButton &&
              !course.closed &&
              !canViewClosingQuestionnaire && (
                <Link
                  href={closeCourse(course.id).url}
                  className="w-fit font-sans text-sm text-deep-red underline underline-offset-2 hover:text-deep-red/80"
                >
                  Cerrar curso
                </Link>
              )}
            {showReopenButton && !!course.closed && (
              <button
                type="button"
                onClick={reopenCourseConfirm}
                className="w-fit font-sans text-sm text-steel underline underline-offset-2 hover:text-steel/80"
              >
                Reabrir curso
              </button>
            )}
          </div>

          <div className="overflow-hidden rounded-3xl border border-ink/15 bg-white shadow-sm">
            <div className="relative h-44 bg-steel sm:h-56">
              {course.house_image_url && (
                <img
                  src={course.house_image_url}
                  alt={(course.house_name as string) || "Casa"}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
              <div
                className="absolute inset-0 bg-top bg-no-repeat opacity-50"
                style={{
                  backgroundImage: `url(${decoratorWhite})`,
                  backgroundSize: "auto 50%",
                }}
              />
              {!!course.identifier && (
                <p className="absolute bottom-3 left-4 w-fit rounded-md bg-black/50 px-2 py-1 font-sans text-sm font-bold uppercase tracking-wide text-white">
                  Código: {course.identifier}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-4 p-5 sm:p-6 md:flex-row md:items-start md:justify-between">
              <div className="flex flex-col gap-2">
                <h1 className="font-sans text-2xl font-semibold text-ink uppercase">
                  {(course.category_name as string) || "-"}
                </h1>
                {course.description && (
                  <p className="flex items-start gap-2 font-sans text-sm text-ink">
                    <InfoIcon className="h-4 w-4 shrink-0 text-ink" />
                    <span className="whitespace-pre-line">
                      {course.description}
                    </span>
                  </p>
                )}
                <p className="flex items-center gap-2 font-sans text-sm text-ink">
                  <CalendarIcon className="h-4 w-4 shrink-0 text-ink" />
                  <span>
                    {formatDateTimeRange(
                      course.start_date,
                      course.end_date,
                      course.start_time,
                      course.end_time,
                    )}
                  </span>
                </p>
                <p className="flex items-center gap-2 font-sans text-sm text-ink">
                  <PinIcon className="h-4 w-4 shrink-0 text-ink" />
                  <span>
                    {[course.house_name, course.house_address]
                      .filter(Boolean)
                      .join(", ") || "-"}
                  </span>
                </p>
              </div>
              <div className="flex flex-col items-start gap-3 md:items-end">
                <p className="font-sans text-sm font-bold text-ink">
                  Aporte: {formatAmount(course.amount)}
                </p>
                {isBackofficeStaff && <CopyCourseInfoButton course={course} />}
                {canSelfEnroll && !course.closed && !readOnly && (
                  <button
                    type="button"
                    onClick={enroll}
                    className="w-fit rounded-full bg-steel px-6 py-2.5 font-sans text-sm font-semibold uppercase tracking-wide text-white hover:bg-steel/90"
                  >
                    Inscríbete aquí
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <InfoSection
              icon={<MoneyIcon />}
              title="Cupos y aportes"
              action={<CopyPaymentInfoButton course={course} />}
            >
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <StatBox
                  value={course.quota ?? "-"}
                  label="Cupos disponibles"
                />
                <StatBox
                  value={course.max_quota ?? "-"}
                  label="Cupos máximos"
                />
                <StatBox
                  value={(course.enrollments_summary as string) || "-"}
                  label="Inscripciones"
                />
                <StatBox value={formatAmount(course.amount)} label="Aporte" />
                {(() => {
                  const percentage = contributionPercentage(
                    course.total_paid_amount as number | null,
                    course.amount as number | null,
                    (course.final_inscriptions as number | null) ??
                      (course.enrollments_count as number | null),
                  );
                  const tone = contributionTone(percentage);
                  return (
                    <>
                      <StatBox
                        value={formatAmount(
                          course.total_paid_amount as number | null,
                        )}
                        label="Total recaudado"
                        tone={tone}
                      />
                      <StatBox
                        value={`${percentage}%`}
                        label="% de aporte"
                        tone={tone}
                      />
                    </>
                  );
                })()}
              </div>
            </InfoSection>

            <InfoSection icon={<BuildingIcon />} title="Organización">
              <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                <Item label="Casa">{(course.house_name as string) || "-"}</Item>
                <Item label="Dirección">
                  {(course.house_address as string) || "-"}
                </Item>
                <Item label="URL Google Maps">
                  {course.house_address_url ? (
                    <a
                      href={course.house_address_url as string}
                      target="_blank"
                      rel="noreferrer"
                      className="w-fit break-all text-steel underline underline-offset-2 hover:text-steel/80"
                    >
                      {course.house_address_url as string}
                    </a>
                  ) : (
                    "-"
                  )}
                </Item>
                <Item label="Centro a cargo">
                  {(course.center_name as string) || "-"}
                </Item>
                <Item label="Cuenta Institucional Encargada/o">
                  {(course.director_name as string) || "-"}
                </Item>
                {isAdmin && (
                  <Item label="Encargada/o de actividad">
                    {(course.responsible_user_name as string) || "-"}
                  </Item>
                )}
                <Item label="Curso(s) para">
                  {course.user_category
                    ? (USER_CATEGORY_LABELS[course.user_category] ??
                      course.user_category)
                    : "-"}
                </Item>
              </dl>
            </InfoSection>

            <InfoSection
              icon={<CalendarIcon />}
              title="Recordatorios confirmación"
            >
              <ul className="list-inside list-disc font-sans text-sm text-ink/80">
                <li>
                  Primer recordatorio:{" "}
                  {formatReminderStatus(first_reminder_day)}
                </li>
                <li>
                  Segundo recordatorio:{" "}
                  {formatReminderStatus(second_reminder_day)}
                </li>
                <li>
                  {(course.confirmed_enrollments_count as number) ?? 0} /{" "}
                  {course.max_quota ?? "-"} inscripciones confirmadas.
                </li>
              </ul>
              {canEditCourse &&
                !course.closed &&
                !readOnly &&
                enrollmentRows.length > 0 && (
                  <button
                    type="button"
                    onClick={sendConfirmationEmailsConfirm}
                    className="w-fit rounded-full bg-steel px-5 py-2 font-sans text-sm font-semibold text-white hover:bg-steel/90"
                  >
                    Enviar emails de confirmación
                  </button>
                )}
            </InfoSection>

            <InfoSection icon={<ToggleIcon />} title="Visibilidad">
              <div className="flex flex-wrap gap-2">
                <StatusBadge active={!!course.published} label="Publicado" />
                <StatusBadge active={!!course.is_public} label="Público" />
                <StatusBadge
                  active={!!course.enable_waitlist}
                  label="Lista de espera habilitada"
                />
                <StatusBadge active={!!course.closed} label="Cerrado" />
              </div>
            </InfoSection>

            {isBackofficeStaff && (
              <InfoSection icon={<DietIcon />} title="Resumen dietas">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                  {diet_options.map((option) => (
                    <StatBox
                      key={option}
                      value={diet_summary?.[option] ?? 0}
                      label={DIET_LABELS[option] ?? option}
                    />
                  ))}
                </div>
                <StatBox
                  value={(course.confirmed_enrollments_count as number) ?? 0}
                  label="Total confirmadas"
                  tone="highlight"
                />
              </InfoSection>
            )}

            {canViewClosingQuestionnaire && (
              <InfoSection
                icon={<InfoIcon />}
                title="Respuestas de cierre"
                action={
                  showCloseButton &&
                  !course.closed && (
                    <LinkButton
                      href={closeCourse(course.id).url}
                      variant="danger"
                      className="px-4! py-1.5! text-sm!"
                    >
                      Cerrar curso
                    </LinkButton>
                  )
                }
              >
                <dl className="flex flex-col gap-3">
                  {COURSE_FORM_QUESTIONS.map(({ key, label }) => (
                    <Item key={key} label={label}>
                      {(course_form?.[key] as string | null) || "-"}
                    </Item>
                  ))}
                  {!course.is_public && (
                    <Item label="Planilla Excel">
                      {closing_file ? (
                        <a
                          href={closing_file.url}
                          download
                          className="w-fit text-steel underline underline-offset-2 hover:text-steel/80"
                        >
                          {closing_file.filename}
                        </a>
                      ) : (
                        "-"
                      )}
                    </Item>
                  )}
                </dl>
                {isAdmin && !readOnly && !course.is_public && (
                  <label className="w-fit cursor-pointer rounded-full border-2 border-ink px-4 py-1.5 font-sans text-sm text-ink hover:border-steel">
                    {closing_file ? "Reemplazar Excel" : "Subir Excel"}
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      className="hidden"
                      onChange={(e) => uploadClosingFile(e.target.files?.[0])}
                    />
                  </label>
                )}
              </InfoSection>
            )}
          </div>
        </div>

        {canViewParticipantTables && (
          <section>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4 px-6 sm:px-8 lg:px-12">
              <h2 className="font-sans text-xl font-semibold text-ink">
                Inscripciones
              </h2>
              <div className="flex items-center gap-4">
                {canExportParticipants && enrollmentRows.length > 0 && (
                  <a
                    href={exportParticipantsExcelCourse(course.id).url}
                    className="w-fit font-sans text-sm text-steel underline underline-offset-2 hover:text-steel/80"
                  >
                    {t("exportarInscritas")}
                  </a>
                )}
                {canEnrollParticipants &&
                  !course.closed &&
                  !readOnly &&
                  ((course.quota as number) > 0 || isAdmin) && (
                    <Button
                      type="button"
                      className="px-5 py-2 text-sm"
                      onClick={() => setAddEnrollmentOpen(true)}
                    >
                      Agregar inscripción
                    </Button>
                  )}
              </div>
            </div>

            <DataTable
              columns={enrollmentColumns}
              rows={enrollmentRows}
              keyExtractor={(e) => e.id}
              actions={enrollmentActions}
              emptyMessage="No hay inscripciones para esta actividad."
            />
          </section>
        )}

        {canViewParticipantTables && !!course.enable_waitlist && (
          <section>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4 px-6 sm:px-8 lg:px-12">
              <h2 className="font-sans text-xl font-semibold text-ink">
                Lista de espera
              </h2>
              {canEnrollParticipants && !course.closed && !readOnly && (
                <Button
                  type="button"
                  className="px-5 py-2 text-sm"
                  onClick={() => setAddWaitListOpen(true)}
                >
                  Agregar a lista de espera
                </Button>
              )}
            </div>

            <DataTable
              columns={waitListColumns}
              rows={waitListRows}
              keyExtractor={(w) => w.id}
              actions={waitListActions}
              emptyMessage="No hay nadie en la lista de espera."
            />
          </section>
        )}
      </div>

      <PaymentModal
        key={paymentTarget?.id ?? "payment-modal"}
        enrollment={paymentTarget}
        onClose={() => setPaymentTarget(null)}
      />
      <DietModal
        key={dietTarget?.id ?? "diet-modal"}
        enrollment={dietTarget}
        dietOptions={diet_options}
        onClose={() => setDietTarget(null)}
      />
      <AddPersonModal
        open={addEnrollmentOpen}
        title="Agregar inscripción"
        submitLabel="Inscribir"
        paramKey="enrollment"
        url={enrollments.create().url}
        courseId={course.id}
        users={users}
        dietOptions={diet_options}
        onClose={() => setAddEnrollmentOpen(false)}
      />
      {!!course.enable_waitlist && (
        <AddPersonModal
          open={addWaitListOpen}
          title="Agregar a lista de espera"
          submitLabel="Agregar"
          paramKey="wait_list"
          url={waitLists.createForStudent().url}
          courseId={course.id}
          users={users}
          dietOptions={diet_options}
          onClose={() => setAddWaitListOpen(false)}
        />
      )}
      <ConfirmDialog
        open={confirmConfig !== null}
        title={confirmConfig?.title ?? ""}
        message={confirmConfig?.message ?? ""}
        confirmLabel={confirmConfig?.confirmLabel}
        variant={confirmConfig?.variant}
        onConfirm={() => confirmConfig?.onConfirm()}
        onCancel={() => setConfirmConfig(null)}
      />
    </>
  );
}

function InfoIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" d="M12 11v5.5M12 8v.01" />
    </svg>
  );
}

function CalendarIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="3.5" y="4.5" width="17" height="16" rx="2" />
      <path strokeLinecap="round" d="M3.5 9h17M8 3v3M16 3v3" />
    </svg>
  );
}

function PinIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path d="M12 21s-6.5-5.5-6.5-10.5a6.5 6.5 0 0 1 13 0C18.5 15.5 12 21 12 21Z" />
      <circle cx="12" cy="10.5" r="2.5" />
    </svg>
  );
}

function MoneyIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path
        strokeLinecap="round"
        d="M9.5 15.5c0 1 .9 1.5 2.5 1.5s2.5-.6 2.5-1.6-1-1.4-2.5-1.7-2.5-.7-2.5-1.7S10.4 10.5 12 10.5s2.5.5 2.5 1.5M12 9v1.2M12 16.8V18"
      />
    </svg>
  );
}

function BuildingIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="4" y="3" width="12" height="18" rx="1" />
      <path strokeLinecap="round" d="M8 7h4M8 11h4M8 15h4M16 10h4v11h-4" />
    </svg>
  );
}

function DietIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
      <path d="M7 2v20" />
      <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
    </svg>
  );
}

function ToggleIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="3" y="8" width="18" height="8" rx="4" />
      <circle cx="8" cy="12" r="2.5" />
    </svg>
  );
}

const MODAL_INPUT =
  "w-full rounded-full border-2 border-ink px-4 py-2.5 font-sans text-sm text-ink focus:border-steel focus:outline-none focus:ring-2 focus:ring-steel/30";

function PaymentModal({
  enrollment,
  onClose,
}: {
  enrollment: Enrollment | null;
  onClose: () => void;
}) {
  const form = useForm({ paid_amount: "", has_payed: false });

  if (!enrollment) return null;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!enrollment) return;
    form.transform((data) => ({
      enrollment: { ...data, has_payed: true },
    }));
    form.post(enrollments.addPayment(enrollment.id).url, {
      preserveScroll: true,
      onSuccess: onClose,
    });
  }

  return (
    <Modal open onClose={onClose} title="Abonar">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <label className="font-sans text-sm text-ink">
          Monto pagado
          <input
            type="number"
            min={0}
            required
            className={`${MODAL_INPUT} mt-1`}
            value={form.data.paid_amount}
            onChange={(e) => form.setData("paid_amount", e.target.value)}
          />
        </label>
        <Button
          type="submit"
          disabled={form.processing}
          className="self-start px-6 py-2 text-sm"
        >
          Guardar
        </Button>
      </form>
    </Modal>
  );
}

function DietModal({
  enrollment,
  dietOptions,
  onClose,
}: {
  enrollment: Enrollment | null;
  dietOptions: string[];
  onClose: () => void;
}) {
  const form = useForm<{ diet: string[] }>({
    diet: Array.isArray(enrollment?.diet) ? (enrollment?.diet as string[]) : [],
  });

  if (!enrollment) return null;

  function select(option: string) {
    form.setData("diet", [option]);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!enrollment) return;
    form.transform((data) => ({ enrollment: data }));
    form.patch(enrollments.updateDiet(enrollment.id).url, {
      preserveScroll: true,
      onSuccess: onClose,
    });
  }

  return (
    <Modal open onClose={onClose} title="Editar dieta">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          {dietOptions.map((option) => (
            <label
              key={option}
              className="flex items-center gap-2 font-sans text-sm text-ink"
            >
              <input
                type="radio"
                name="diet-modal"
                checked={form.data.diet.includes(option)}
                onChange={() => select(option)}
              />
              {DIET_LABELS[option] ?? option}
            </label>
          ))}
        </div>
        <Button
          type="submit"
          disabled={form.processing}
          className="self-start px-6 py-2 text-sm"
        >
          Guardar
        </Button>
      </form>
    </Modal>
  );
}

function AddPersonModal({
  open,
  title,
  submitLabel,
  paramKey,
  url,
  courseId,
  users,
  dietOptions,
  onClose,
}: {
  open: boolean;
  title: string;
  submitLabel: string;
  paramKey: "enrollment" | "wait_list";
  url: string;
  courseId: number;
  users: { id: number; name: string; rut: string }[];
  dietOptions: string[];
  onClose: () => void;
}) {
  const form = useForm<{ course_id: number; user_id: string; diet: string[] }>({
    course_id: courseId,
    user_id: "",
    diet: dietOptions.includes(DEFAULT_DIET) ? [DEFAULT_DIET] : [],
  });

  if (!open) return null;

  function select(option: string) {
    form.setData("diet", [option]);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    form.transform((data) => ({ [paramKey]: data }));
    form.post(url, {
      preserveScroll: true,
      onSuccess: () => {
        onClose();
        form.reset();
      },
    });
  }

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="font-sans text-sm text-ink">Usuario</span>
          <SearchableSelect
            className={MODAL_INPUT}
            value={form.data.user_id}
            onChange={(v) => form.setData("user_id", v)}
            options={users.map((u) => ({
              value: String(u.id),
              label: `${u.name} (${u.rut})`,
            }))}
            placeholder="Selecciona un usuario"
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-sans text-sm text-ink">Dieta</span>
          {dietOptions.map((option) => (
            <label
              key={option}
              className="flex items-center gap-2 font-sans text-sm text-ink"
            >
              <input
                type="radio"
                name="diet-add-person"
                checked={form.data.diet.includes(option)}
                onChange={() => select(option)}
              />
              {DIET_LABELS[option] ?? option}
            </label>
          ))}
        </div>

        <Button
          type="submit"
          disabled={form.processing}
          className="self-start px-6 py-2 text-sm"
        >
          {submitLabel}
        </Button>
      </form>
    </Modal>
  );
}
